/**
 * CRUCIBLE Tool Definitions
 *
 * These define the UI tools that can be triggered by AI responses.
 * The backend sends structured tool_result events that the frontend
 * processes to update tabs and UI state.
 */

import { z } from 'zod'

// Tool result schemas for type-safe handling
export const UpdateOdmXmlSchema = z.object({
  tool: z.literal('update_odm_xml'),
  content: z.string(),
  isFragment: z.boolean().default(false),
  title: z.string().default('ODM XML'),
})

export const ShowFormPreviewSchema = z.object({
  tool: z.literal('show_form_preview'),
  formXml: z.string(),
  formName: z.string(),
  formOid: z.string().optional(),
})

export const UpdateStudyConfigSchema = z.object({
  tool: z.literal('update_study_config'),
  config: z.union([z.string(), z.record(z.string(), z.unknown())]),
  title: z.string().default('Study Configuration'),
})

export const AddToCanvasSchema = z.object({
  tool: z.literal('add_to_canvas'),
  content: z.string(),
  title: z.string(),
  sectionId: z.string().optional(),
})

/**
 * Decode a JSON-encoded array before validating it.
 *
 * `workUnits` / `resourceOwnership` are NOT in present_build_plan's backend
 * input_schema, but models still send them -- and a field outside the schema
 * arrives as a JSON STRING rather than an array (seen live 2026-09-27 on
 * claude-sonnet-5: `workUnits: "[{\"unit_id\": \"U1\", ...}]"`). The array
 * schema then failed, and parseToolResult DROPPED the whole plan: the Build
 * tab kept showing the previous plan while the agent believed it had updated
 * it. The handler never even reads these two fields (the real values come from
 * POST /api/parallel-build/plan).
 *
 * Decoding -- rather than ignoring the value -- keeps the shape guard below
 * meaningful: a string that decodes to the wrong step-based shape is still
 * rejected, exactly as the same array would be.
 */
function decodeJsonArrayString(value: unknown): unknown {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

export const PresentBuildPlanSchema = z.object({
  tool: z.literal('present_build_plan'),
  structureSummary: z.string(),
  // Optional — some plans omit per-form detail. When present, each entry
  // must at least carry form_oid/form_name; extra fields (crf_text,
  // expectedItemCount, ...) pass through untouched.
  //
  // crf_text is deliberately NOT required here even though the backend tool
  // schema (crucible_tools.py) requires it on every forms entry — that's a
  // model-steering nudge at the generation boundary (Claude's tool_use is
  // guided, not hard-enforced, by input_schema.required), not a client-side
  // validation contract. Requiring it here too would reject the whole plan
  // (routing to the "unsupported tool result" fallback) whenever the model
  // still omits it, turning a soft build-quality issue into a hard render
  // failure. Keep this lenient; let a missing crf_text degrade to weaker
  // dependency-clustering downstream instead of no plan at all.
  forms: z
    .array(z.object({ form_oid: z.string(), form_name: z.string() }).passthrough())
    .optional(),
  // present_build_plan's backend tool schema no longer asks the model for
  // these (see crucible_tools.py) -- the real work_units/resource_ownership
  // now come from POST /api/parallel-build/plan (the same deterministic
  // compute_plan() the build itself uses), fetched and merged into the
  // canvas tab content AFTER this tool result is parsed. Optional/defaulted
  // here purely so a stale/older persisted plan (saved before this change)
  // that still carries these fields doesn't fail parsing.
  workUnits: z.preprocess(
    decodeJsonArrayString,
    z
      .array(z.object({ unit_id: z.string(), form_oids: z.array(z.string()) }).passthrough())
      .optional()
      .default([]),
  ),
  resourceOwnership: z.preprocess(
    decodeJsonArrayString,
    z
      .array(z.object({ resource_oid: z.string(), owner_unit_id: z.string() }).passthrough())
      .optional()
      .default([]),
  ),
  openQuestions: z.array(
    z.object({ id: z.string(), text: z.string(), resolution: z.unknown().optional() }).passthrough(),
  ),
  title: z.string().default('Build Plan'),
})

export const DisplayStudySchema = z.object({
  tool: z.literal('display_study'),
  fileId: z.string(),
  message: z.string().optional(),
})

export const CompareOdmSchema = z.object({
  tool: z.literal('compare_odm'),
  leftXml: z.string(),
  rightXml: z.string(),
  leftLabel: z.string().default('Original'),
  rightLabel: z.string().default('Modified'),
  title: z.string().default('ODM Comparison'),
})

export const GenerateStudyShellSchema = z.object({
  tool: z.literal('generate_study_shell'),
  /**
   * The protocol in the user's own words. The shell worker derives study
   * identity AND every StudyEventDef from this, so a protocol with no visit
   * schedule yields a shell with no events — hence required, not optional.
   */
  protocol_text: z.string(),
  /**
   * crf_text is deliberately lenient here for the same reason it is on
   * PresentBuildPlanSchema: `input_schema.required` steers the model, it does
   * not enforce. Rejecting the whole call over a missing crf_text would route
   * to the "unsupported tool result" fallback and produce NO shell, turning
   * weaker dependency-clustering into a hard failure. Identity is required
   * though — a form with neither oid nor name cannot be rendered or owned.
   */
  forms: z.array(
    z.object({
      form_oid: z.string(),
      form_name: z.string(),
      crf_text: z.string().optional(),
    }).passthrough(),
  ),
})

// Union of all tool result types
const TOOL_RESULT_SCHEMAS = [
  UpdateOdmXmlSchema,
  ShowFormPreviewSchema,
  UpdateStudyConfigSchema,
  AddToCanvasSchema,
  DisplayStudySchema,
  CompareOdmSchema,
  PresentBuildPlanSchema,
  GenerateStudyShellSchema,
] as const

export const ToolResultSchema = z.discriminatedUnion('tool', TOOL_RESULT_SCHEMAS)

// The tool names this module renders, derived from the schemas themselves so
// the set cannot drift from the union. Used to tell two very different things
// apart in parseToolResult:
//   - "the model called a tool we don't render" — routine; every ordinary
//     backend tool (pubmed_search, cdash_domain, ...) lands here because
//     use-chat.ts pipes EVERY tool call through parseToolResult.
//   - "a tool we DO render sent a payload we rejected" — a bug: the tool ran,
//     it shows up in tool_stats, and its tab/canvas never appears.
const KNOWN_UI_TOOL_NAMES: ReadonlySet<string> = new Set(
  TOOL_RESULT_SCHEMAS.map((schema) => schema.shape.tool.value as string),
)

export type ToolResult = z.infer<typeof ToolResultSchema>

// Helper to check if a parsed object is a tool result
export function isToolResult(obj: unknown): obj is ToolResult {
  const result = ToolResultSchema.safeParse(obj)
  return result.success
}

// Parse tool results from various formats
export function parseToolResult(data: unknown): ToolResult | null {
  // Handle CRUCIBLE_ACTION format (legacy)
  if (typeof data === 'object' && data !== null && 'action' in data) {
    const action = data as Record<string, unknown>

    // Map action types to tool types
    if (action.action === 'display_study') {
      return {
        tool: 'display_study',
        fileId: String(action.file_id || ''),
        message: action.message ? String(action.message) : undefined,
      }
    }

    if (action.action === 'display_form') {
      return {
        tool: 'show_form_preview',
        formXml: '', // Will be fetched by file_id
        formName: String(action.form_oid || 'Form'),
        formOid: action.form_oid ? String(action.form_oid) : undefined,
      }
    }
  }

  // Try parsing as tool result directly
  const result = ToolResultSchema.safeParse(data)
  if (result.success) {
    return result.data
  }

  // A rejected payload for a tool we DO render is the "silently vanished tool"
  // bug: the model called it, the backend counted it in tool_stats, and the UI
  // effect never happened. Returning a bare `null` here — no log, no error, no
  // telemetry — is why "present_build_plan ran but no Build tab appeared" was
  // undiagnosable from logs. Log the tool and the failing fields.
  //
  // Deliberately still returns null rather than throwing: both callers branch
  // on falsy (use-chat.ts's onToolCall and extractToolResults below), and
  // throwing would escalate a degraded tool result into a dead chat turn.
  // This is observability, not a behaviour change.
  const toolName =
    typeof data === 'object' && data !== null && typeof (data as { tool?: unknown }).tool === 'string'
      ? (data as { tool: string }).tool
      : undefined

  if (toolName && KNOWN_UI_TOOL_NAMES.has(toolName)) {
    console.error(
      `[Tools] parseToolResult DROPPED a "${toolName}" result — payload failed its schema. ` +
        'The tool ran, but its tab/canvas will never appear.',
      {
        tool: toolName,
        issues: result.error.issues.map((issue) => ({
          field: issue.path.join('.') || '(root)',
          code: issue.code,
          message: issue.message,
        })),
        receivedKeys: Object.keys(data as Record<string, unknown>),
      },
    )
  }
  // An unknown discriminator is NOT logged. parseToolResult is the funnel for
  // every tool call in the app, so most misses here are ordinary backend tools
  // with no UI effect — logging them would bury the signal above in noise.

  return null
}

/**
 * Extract tool results from text content.
 *
 * @deprecated This regex-based extraction is now a fallback only.
 * Primary tool handling is via SSE stream interception in use-chat.ts.
 * The backend now emits tool-call and tool-result events separately from text,
 * which are intercepted and executed immediately via the custom fetch wrapper.
 *
 * This function is kept for:
 * 1. Loading chat history (where tools are still embedded in saved text)
 * 2. Fallback if SSE interception fails
 */
export function extractToolResults(content: string): {
  tools: ToolResult[]
  cleanedContent: string
} {
  const tools: ToolResult[] = []
  let cleaned = content

  // Extract CRUCIBLE_TOOL_RESULT blocks (legacy format from text embedding)
  console.log('[Tools] extractToolResults (fallback) called, content length:', content.length, 'contains CRUCIBLE_TOOL_RESULT:', content.includes('CRUCIBLE_TOOL_RESULT'))

  // Debug: Check for HTML-escaped versions
  const hasEscaped = content.includes('&lt;CRUCIBLE_TOOL_RESULT&gt;')
  const hasBackslash = content.includes('\\<CRUCIBLE_TOOL_RESULT')
  console.log('[Tools] HTML escaped:', hasEscaped, 'Backslash escaped:', hasBackslash)

  // If HTML escaped, unescape first
  let contentToMatch = cleaned
  if (hasEscaped) {
    contentToMatch = contentToMatch
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
    console.log('[Tools] Unescaped content for matching')
  }

  const toolResultMatches = [...contentToMatch.matchAll(/<CRUCIBLE_TOOL_RESULT>([\s\S]*?)<\/CRUCIBLE_TOOL_RESULT>/gi)]
  console.log('[Tools] Regex matches found:', toolResultMatches.length)

  // Use unescaped content for further processing if we unescaped
  if (hasEscaped) {
    cleaned = contentToMatch
  }

  for (const match of toolResultMatches) {
    try {
      const resultJson = JSON.parse(match[1].trim())
      const toolResult = parseToolResult(resultJson)
      if (toolResult) {
        tools.push(toolResult)
        console.log('[Tools] Extracted native tool result:', toolResult.tool)
      }
    } catch (e) {
      console.warn('[Tools] Failed to parse CRUCIBLE_TOOL_RESULT:', e)
    }
    cleaned = cleaned.replace(match[0], '')
  }

  // Extract CRUCIBLE_ACTION blocks (legacy format)
  const actionMatches = [...cleaned.matchAll(/<CRUCIBLE_ACTION>([\s\S]*?)<\/CRUCIBLE_ACTION>/gi)]
  for (const match of actionMatches) {
    try {
      const actionJson = JSON.parse(match[1].trim())
      const toolResult = parseToolResult(actionJson)
      if (toolResult) {
        tools.push(toolResult)
      }
    } catch (e) {
      console.warn('[Tools] Failed to parse CRUCIBLE_ACTION:', e)
    }
    cleaned = cleaned.replace(match[0], '')
  }

  // Extract ODM_UPDATE blocks -> update_odm_xml tool
  const odmMatches = [...cleaned.matchAll(/<ODM_UPDATE>([\s\S]*?)<\/ODM_UPDATE>/gi)]
  for (const match of odmMatches) {
    const xmlContent = match[1].trim()
    if (xmlContent) {
      tools.push({
        tool: 'update_odm_xml',
        content: xmlContent,
        isFragment: true,
        title: 'ODM Update',
      })
    }
    cleaned = cleaned.replace(match[0], '')
  }

  // Extract XML content - but be smart about it!
  // We want to detect ACTUAL ODM XML documents, not markdown with XML code examples.
  //
  // Route to XML Tab (parse/render):
  //   - Has XML declaration (<?xml) or ODM root with xmlns
  //   - Complete ODM document structure
  //   - Standalone ```xml block that contains actual ODM (not code examples)
  //
  // Keep in Canvas (conversation):
  //   - Markdown with inline XML snippets in code blocks for documentation
  //   - Design docs with TypeScript/JS code containing XML-like tags
  //   - Explanatory content that references ODM elements

  // Helper to check if content inside a code block is actual ODM vs code example
  const isActualOdmXml = (xml: string): boolean => {
    const trimmed = xml.trim()
    // Must have XML declaration OR ODM root with proper structure
    const hasXmlDeclaration = trimmed.startsWith('<?xml')
    const hasOdmRoot = /<ODM\s+[^>]*xmlns/i.test(trimmed) || trimmed.startsWith('<ODM')
    const hasClosingOdm = trimmed.includes('</ODM>')

    // If it has XML declaration or proper ODM root, it's likely real XML
    if (hasXmlDeclaration) return true
    if (hasOdmRoot && hasClosingOdm) return true

    // Check for complete ODM element fragments (not just references in code)
    // Must have both opening AND closing tags for a top-level ODM element
    const odmElements = ['FormDef', 'ItemGroupDef', 'ItemDef', 'CodeList', 'StudyEventDef', 'MetaDataVersion']
    for (const el of odmElements) {
      const hasOpening = new RegExp(`<${el}\\s+[^>]*OID=`, 'i').test(trimmed)
      const hasClosing = new RegExp(`</${el}>`, 'i').test(trimmed)
      if (hasOpening && hasClosing) {
        // Additional check: real ODM elements have OID attributes
        return true
      }
    }

    return false
  }

  // First, check for ```xml code blocks
  const xmlCodeBlockMatch = cleaned.match(/```(?:xml|XML)\s*([\s\S]*?)```/i)
  if (xmlCodeBlockMatch && xmlCodeBlockMatch[1]) {
    const xmlContent = xmlCodeBlockMatch[1].trim()

    // Only extract if it's actual ODM XML, not a code example
    if (isActualOdmXml(xmlContent)) {
      const isFragment = !xmlContent.includes('<?xml') && !xmlContent.startsWith('<ODM')
      tools.push({
        tool: 'update_odm_xml',
        content: xmlContent,
        isFragment,
        title: 'ODM XML',
      })
      cleaned = cleaned.replace(xmlCodeBlockMatch[0], '').trim()
    }
    // If it's not actual ODM, leave it in the content for canvas display
  }

  // Check for standalone ODM documents (not in code blocks)
  // Only trigger on strong indicators: XML declaration or complete ODM document
  const hasStrongXmlIndicator =
    (cleaned.includes('<?xml') && cleaned.includes('<ODM')) ||
    (cleaned.includes('<ODM') && cleaned.includes('xmlns') && cleaned.includes('</ODM>'))

  if (hasStrongXmlIndicator) {
    // Try complete ODM document
    const xmlDirectMatch = cleaned.match(/<\?xml[\s\S]*?<\/ODM>/g) ||
      cleaned.match(/<ODM\s+[^>]*xmlns[\s\S]*?<\/ODM>/g)

    if (xmlDirectMatch && xmlDirectMatch[0]) {
      tools.push({
        tool: 'update_odm_xml',
        content: xmlDirectMatch[0].trim(),
        isFragment: false,
        title: 'ODM XML',
      })
      cleaned = cleaned.replace(xmlDirectMatch[0], '').trim()
    }
  }

  // Extract JSON code blocks -> update_study_config tool
  const jsonCodeBlockMatch = cleaned.match(/```(?:json|JSON)\s*([\s\S]*?)```/i)
  if (jsonCodeBlockMatch && jsonCodeBlockMatch[1]) {
    tools.push({
      tool: 'update_study_config',
      config: jsonCodeBlockMatch[1].trim(),
      title: 'Study Configuration',
    })
    cleaned = cleaned.replace(jsonCodeBlockMatch[0], '')
  }

  // Check for structured markdown -> add_to_canvas tool
  const hasTable = cleaned.includes('|') && cleaned.includes('---')
  const hasMultipleHeaders = (cleaned.match(/^#+\s/gm) || []).length >= 3

  if (hasTable || hasMultipleHeaders) {
    // Extract the first header as title
    const headerMatch = cleaned.match(/^#+\s+(.+?)$/m)
    // Generic title — the dispatcher in use-chat.ts will retag this with
    // the correct persona-aware title when it writes to the canvas. The
    // text-marker fallback (this function) is mostly legacy; non-concierge
    // callers override the cleanedContent below with a dynamic
    // canvasReferenceMarkdown that knows which persona's canvas applies.
    const title = headerMatch?.[1]?.trim() || 'Canvas'

    tools.push({
      tool: 'add_to_canvas',
      content: cleaned.trim(),
      title,
    })

    // Generic placeholder — caller substitutes persona-aware text. Kept as a
    // no-target stub so any path that falls through gets a non-misleading
    // label rather than a broken "Study Design Canvas" pointer.
    const summary = headerMatch?.[1] || (hasTable ? 'Table generated' : 'View structured content')
    cleaned = `**${summary}**\n\n[View in Canvas]`
  }

  // Strip any remaining internal blocks
  cleaned = cleaned.replace(/<CRUCIBLE_TOOL_REQUEST>[\s\S]*?<\/CRUCIBLE_TOOL_REQUEST>/gi, '')

  return {
    tools,
    cleanedContent: cleaned.trim(),
  }
}

// Tool execution handler type
export type ToolHandler = {
  update_odm_xml: (params: z.infer<typeof UpdateOdmXmlSchema>) => void
  show_form_preview: (params: z.infer<typeof ShowFormPreviewSchema>) => void
  update_study_config: (params: z.infer<typeof UpdateStudyConfigSchema>) => void
  add_to_canvas: (params: z.infer<typeof AddToCanvasSchema>) => void
  display_study: (params: z.infer<typeof DisplayStudySchema>) => void
  compare_odm: (params: z.infer<typeof CompareOdmSchema>) => void
  present_build_plan: (params: z.infer<typeof PresentBuildPlanSchema>) => void
  generate_study_shell: (params: z.infer<typeof GenerateStudyShellSchema>) => void
}

// Execute tool with handler
export function executeTool(tool: ToolResult, handler: ToolHandler): void {
  switch (tool.tool) {
    case 'update_odm_xml':
      handler.update_odm_xml(tool)
      break
    case 'show_form_preview':
      handler.show_form_preview(tool)
      break
    case 'update_study_config':
      handler.update_study_config(tool)
      break
    case 'add_to_canvas':
      handler.add_to_canvas(tool)
      break
    case 'display_study':
      handler.display_study(tool)
      break
    case 'compare_odm':
      handler.compare_odm(tool)
      break
    case 'present_build_plan':
      handler.present_build_plan(tool)
      break
    case 'generate_study_shell':
      handler.generate_study_shell(tool)
      break
  }
}
